import { useCallback, useMemo, useState } from "react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, format, isSameMonth,
  addMonths, subMonths, addWeeks, subWeeks, isToday,
} from "date-fns";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useContentStore, CONTENT_STAGES, STAGE_COLOR_MAP, EMPTY_ITEMS, type ContentItem } from "@/stores/contentStore";
import { useToast } from "@/hooks/use-toast";
import { NewArticleModal } from "./NewArticleModal";
import { BatchPlanningModal } from "./BatchPlanningModal";
import { cn } from "@/lib/utils";

interface ContentCalendarProps {
  projectId: number;
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function ContentCalendar({ projectId }: ContentCalendarProps) {
  const items = useContentStore((s) => s.items[projectId] ?? EMPTY_ITEMS);
  const calendarView = useContentStore((s) => s.calendarView);
  const setCalendarView = useContentStore((s) => s.setCalendarView);
  const selectItem = useContentStore((s) => s.selectItem);
  const rescheduleItem = useContentStore((s) => s.rescheduleItem);
  const { toast } = useToast();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [dragOverDate, setDragOverDate] = useState<string | null>(null);
  const [newArticleDate, setNewArticleDate] = useState<string | null>(null);
  const [showBatchModal, setShowBatchModal] = useState(false);

  // Pre-compute items-by-date map (O(N) instead of O(cells*N))
  const itemsByDate = useMemo(() => {
    const map: Record<string, ContentItem[]> = {};
    for (const item of items) {
      const d = item.scheduled_publish_date || item.published_at;
      if (!d) continue;
      const key = d.slice(0, 10); // yyyy-MM-dd
      if (!map[key]) map[key] = [];
      map[key].push(item);
    }
    return map;
  }, [items]);

  const days = useMemo(() => {
    if (calendarView === "month") {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      const start = startOfWeek(monthStart, { weekStartsOn: 1 });
      const end = endOfWeek(monthEnd, { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end });
    }
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: weekStart, end: weekEnd });
  }, [currentDate, calendarView]);

  const navigate = useCallback((dir: number) => {
    setCurrentDate((prev) =>
      calendarView === "month"
        ? dir > 0 ? addMonths(prev, 1) : subMonths(prev, 1)
        : dir > 0 ? addWeeks(prev, 1) : subWeeks(prev, 1)
    );
  }, [calendarView]);

  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    e.dataTransfer.setData("calendarItemId", itemId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrop = async (e: React.DragEvent, day: Date) => {
    e.preventDefault();
    setDragOverDate(null);
    const itemId = e.dataTransfer.getData("calendarItemId");
    if (!itemId) return;

    try {
      await rescheduleItem(itemId, format(day, "yyyy-MM-dd"));
    } catch {
      toast({ title: "Reschedule failed", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" aria-label="Previous period" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <h2 className="text-sm font-medium min-w-[140px] text-center" aria-live="polite">
            {calendarView === "month"
              ? format(currentDate, "MMMM yyyy")
              : `Week of ${format(days[0], "MMM dd")}`}
          </h2>
          <Button variant="ghost" size="icon" aria-label="Next period" onClick={() => navigate(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex gap-1 p-0.5 bg-muted/30 rounded-md" role="group" aria-label="Calendar view">
            <button
              onClick={() => setCalendarView("month")}
              aria-pressed={calendarView === "month"}
              className={cn(
                "px-2 py-1 text-xs rounded",
                calendarView === "month" ? "bg-background shadow-sm" : "text-muted-foreground"
              )}
            >
              Month
            </button>
            <button
              onClick={() => setCalendarView("week")}
              aria-pressed={calendarView === "week"}
              className={cn(
                "px-2 py-1 text-xs rounded",
                calendarView === "week" ? "bg-background shadow-sm" : "text-muted-foreground"
              )}
            >
              Week
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowBatchModal(true)}>
            Schedule Batch
          </Button>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="rounded-lg border border-border/40 overflow-hidden">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 border-b border-border/40">
          {WEEKDAYS.map((d) => (
            <div key={d} className="px-2 py-1.5 text-xs text-muted-foreground text-center font-medium">
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7">
          {days.map((day) => {
            const dayStr = format(day, "yyyy-MM-dd");
            const dayItems = itemsByDate[dayStr] || [];
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isDragOver = dragOverDate === dayStr;

            return (
              <div
                key={dayStr}
                className={cn(
                  "border-b border-r border-border/20 transition-colors",
                  calendarView === "month" ? "min-h-[80px]" : "min-h-[160px]",
                  !isCurrentMonth && "opacity-40",
                  isDragOver && "bg-[rgba(0,178,200,0.06)]",
                  isToday(day) && "bg-blue-500/5"
                )}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverDate(dayStr);
                }}
                onDragLeave={() => setDragOverDate(null)}
                onDrop={(e) => handleDrop(e, day)}
                onClick={() => {
                  if (dayItems.length === 0) {
                    setNewArticleDate(dayStr);
                  }
                }}
              >
                {/* Day number */}
                <div className="flex items-center justify-between px-1.5 py-1">
                  <span className={cn(
                    "text-xs",
                    isToday(day)
                      ? "bg-blue-500 text-white w-5 h-5 rounded-full flex items-center justify-center"
                      : "text-muted-foreground"
                  )}>
                    {format(day, "d")}
                  </span>
                  {dayItems.length === 0 && isCurrentMonth && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setNewArticleDate(dayStr);
                      }}
                      aria-label={`Add article on ${format(day, "MMMM d")}`}
                      className="opacity-0 group-hover:opacity-100 hover:opacity-100 text-muted-foreground hover:text-foreground"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  )}
                </div>

                {/* Article chips */}
                <div className="px-1 pb-1 space-y-0.5">
                  {dayItems.slice(0, calendarView === "month" ? 3 : 8).map((item) => (
                    <div
                      key={item.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, item.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        selectItem(item.id);
                      }}
                      className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs cursor-grab active:cursor-grabbing hover:bg-white/5 truncate"
                    >
                      <div
                        className="w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ backgroundColor: STAGE_COLOR_MAP[item.stage] || "#6B7280" }}
                      />
                      <span className="truncate">{item.title || item.primary_keyword || "Untitled"}</span>
                    </div>
                  ))}
                  {dayItems.length > (calendarView === "month" ? 3 : 8) && (
                    <span className="text-xs text-muted-foreground px-1.5">
                      +{dayItems.length - (calendarView === "month" ? 3 : 8)} more
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-xs text-muted-foreground">
        {CONTENT_STAGES.filter((s) => ["published", "writing", "queued", "scheduled"].includes(s.id)).map((s) => (
          <div key={s.id} className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
            {s.label}
          </div>
        ))}
      </div>

      {/* New Article Modal (from calendar click) */}
      <NewArticleModal
        isOpen={!!newArticleDate}
        onClose={() => setNewArticleDate(null)}
        projectId={projectId}
        prefillDate={newArticleDate || undefined}
      />

      {/* Batch Planning Modal */}
      <BatchPlanningModal
        isOpen={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        projectId={projectId}
      />
    </div>
  );
}
